import React, { useState } from 'react';
import { 
  Button, 
  TextField, 
  Typography, 
  Grid, 
  useMediaQuery, 
  useTheme, 
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  Box
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LockResetIcon from '@mui/icons-material/LockReset';
import { Link, useNavigate, useLocation } from 'react-router-dom';

function PasswordRecovery() {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const steps = ['Enter Email', 'Verify Code', 'New Password'];

  // Função para enviar código de verificação
 // Função para enviar código de verificação
const handleSendCode = async (event) => {
  event.preventDefault();
  setIsLoading(true);
  setErrorMessage('');
  setSuccessMessage('');

  if (!email) {
    setErrorMessage('Please enter your email address.');
    setIsLoading(false);
    return;
  }

  try {
    const response = await fetch('https://api.hub4food.colab4food.com/api/password/send-code', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (response.ok) {
      setSuccessMessage('Verification code sent successfully! Check your email.');
      console.log('Código gerado:', data.code); 
      
      // 🔥 REDIRECIONA PARA A PÁGINA DE CÓDIGO APÓS 1 SEGUNDO
      setTimeout(() => {
        navigate('/password-recovery-code', { 
          state: { email } // Passa o email para a próxima página
        });
      }, 1000);
      
    } else {
      setErrorMessage(data.error || 'Error sending verification code.');
    }
  } catch (error) {
    console.error('Error sending code:', error);
    setErrorMessage('Error sending verification code. Please try again.');
  } finally {
    setIsLoading(false);
  }
};
  // Função para verificar o código
  const handleVerifyCode = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    if (!code) {
      setErrorMessage('Please enter the verification code.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch('https://api.hub4food.colab4food.com/api/password/verify-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code }),
      });

      const data = await response.json();

      if (response.ok && data.valid) {
        setSuccessMessage('Code verified successfully!');
        setActiveStep(2); 
      } else {
        setErrorMessage(data.error || 'Invalid verification code.');
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      setErrorMessage('Error verifying code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Função para redefinir a password
  const handleResetPassword = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    if (!newPassword || !confirmPassword) {
      setErrorMessage('Please fill in all password fields.');
      setIsLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch('https://api.hub4food.colab4food.com/api/password/reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email, 
          code, 
          newPassword, 
          confirmPassword 
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage('Password reset successfully! You can now login with your new password.');
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        setErrorMessage(data.error || 'Error resetting password.');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setErrorMessage('Error resetting password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setErrorMessage('');
    setSuccessMessage('');
  };

  // Estilos inline
   const styles = {
    container: {
      display: 'flex',
      height: '100vh',
      fontFamily: "'Poppins', sans-serif",
    },
    leftSection: {
      backgroundImage: "url('app2.jpg')",
      backgroundSize: 'cover',
      backgroundPosition: 'center center',
      backgroundRepeat: 'no-repeat',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative',
      color: 'rgb(0, 0, 0)',
      padding: '20px',
      minHeight: '100vh',
    },

    leftSectionFooter: {
      position: 'absolute',
      bottom: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      textAlign: 'center',
      backdropFilter: 'blur(5px)',
      backgroundColor: 'rgba(255, 255, 255, 0.6)',
      padding: '10px 20px',
      borderRadius: '10px',
      fontSize: '14px',
      width: '80%',
      maxWidth: '300px',
    },
    typographyH4: {
      fontWeight: 'bold',
      marginBottom: '10px',
      marginTop: '10px',
    },
    rightSection: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center', 
      alignItems: 'center',
      padding: '40px',
      width: '100%', 
    },
    logo: {
      width: '80px',
      marginBottom: '20px',
    },
    formContainer: {
      width: '100%',
      maxWidth: '400px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center', 
    },
    stepper: {
      width: '100%',
      maxWidth: '400px',
      marginBottom: '30px',
    },
    button: {
      width: '100%',
      marginTop: '20px',
      backgroundColor: '#007bff',
      color: 'white',
      textTransform: 'none',
      padding: '10px',
      fontSize: '16px',
      borderRadius: '5px',
      '&:hover': {
        backgroundColor: '#0056b3',
      },
    },
    backButton: {
      width: '100%',
      marginTop: '10px',
      textTransform: 'none',
      padding: '10px',
      fontSize: '16px',
      borderRadius: '5px',
    },
    errorMessage: {
      marginTop: '10px',
      color: 'red',
      fontSize: '14px',
      textAlign: 'center',
    },
    successMessage: {
      marginTop: '10px',
      color: 'green',
      fontSize: '14px',
      textAlign: 'center',
    },
 
    titleContainer: {
      textAlign: 'center',
      marginBottom: '20px',
      width: '100%',
    },
  };

  return (
    <Grid container style={styles.container}>
      {/* Seção da esquerda */}
      <Grid
        item
        xs={12}
        sm={6}
        style={styles.leftSection}
      >
        <Typography 
  variant="h3" 
  gutterBottom
  style={{
    color: '#000000',
    textShadow: `
      0 0 10px #fff,
      0 0 10px #fff,
      0 0 10px #fff,
      0 0 10px #fff
    `,
    fontWeight: 'bold',
    marginBottom: '10px'
  }}
>
  Reset Your Password
</Typography>
<Typography 
  variant="h5" 
  gutterBottom
  style={{
    color: '#000000',
    textShadow: `
      0 0 8px #fff,
      0 0 8px #fff,
      0 0 8px #fff
    `,
    fontWeight: '600',
    marginBottom: '20px'
  }}
>
  Secure your Hub4Food<sup>®</sup> account
</Typography>

        {/* Texto no fundo */}
        <div style={styles.leftSectionFooter}>
          <Typography variant="body2">
            Remember your password?{' '}
            <Link to="/" style={{ color: '#007bff', textDecoration: 'none' }}>
              Back to Login
            </Link>
          </Typography>
        </div>
      </Grid>

      {/* Seção da direita */}
      <Grid
        item
        xs={12}
        sm={6}
        style={styles.rightSection}
      >
        <div style={styles.formContainer}>
          {/* Container para título e logo CENTRALIZADO */}
          <div style={styles.titleContainer}>
            <img
              src="/logotipo.png"
              alt="Hub4Food Logo"
              style={{ ...styles.logo, width: isMobile ? '60px' : '80px' }}
            />
            <Typography variant="h5" gutterBottom>
              Password Recovery
            </Typography>
          </div>

          {/* Stepper */}
          <Stepper activeStep={activeStep} style={styles.stepper}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 1: Enter Email */}
          {activeStep === 0 && (
            <form onSubmit={handleSendCode} style={{ width: '100%' }}>
              <TextField
                label="Email Address"
                type="email"
                variant="outlined"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                margin="normal"
                placeholder="Enter your registered email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
                style={styles.button}
              >
                {isLoading ? 'Sending...' : 'Send Verification Code'}
              </Button>
            </form>
          )}

          {/* Step 2: Verify Code */}
          {activeStep === 1 && (
            <form onSubmit={handleVerifyCode} style={{ width: '100%' }}>
              <TextField
                label="Verification Code"
                variant="outlined"
                fullWidth
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
                margin="normal"
                placeholder="Enter 4-digit code"
                inputProps={{ maxLength: 4 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VpnKeyIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
                style={styles.button}
              >
                {isLoading ? 'Verifying...' : 'Verify Code'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleBack}
                style={styles.backButton}
              >
                Back
              </Button>
            </form>
          )}

          {/* Step 3: New Password */}
          {activeStep === 2 && (
            <form onSubmit={handleResetPassword} style={{ width: '100%' }}>
              <TextField
                label="New Password"
                type="password"
                variant="outlined"
                fullWidth
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                margin="normal"
                placeholder="Enter new password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockResetIcon />
                    </InputAdornment>
                  ),
                }}
                helperText="Must include uppercase, lowercase, number, and special character"
              />
              <TextField
                label="Confirm Password"
                type="password"
                variant="outlined"
                fullWidth
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                margin="normal"
                placeholder="Confirm new password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockResetIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
                style={styles.button}
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleBack}
                style={styles.backButton}
              >
                Back
              </Button>
            </form>
          )}

          {errorMessage && (
            <Typography style={styles.errorMessage}>
              {errorMessage}
            </Typography>
          )}

          {successMessage && (
            <Typography style={styles.successMessage}>
              {successMessage}
            </Typography>
          )}
        </div>
      </Grid>
    </Grid>
  );
}

export default PasswordRecovery;